import { videos, categories, heroSlides, trendingTags } from "@/mocks/videos";
import type { Video, VideoCategory, HeroSlide } from "@/types/video";

export const allVideos = videos as Video[];
export const allCategories = categories as VideoCategory[];
export const allHeroSlides = heroSlides as HeroSlide[];
export const allTrendingTags = trendingTags as string[];

export function getCategoryBySlug(slug?: string): VideoCategory | undefined {
  return allCategories.find((c) => c.slug === slug);
}

export function getVideoById(id?: string): Video | undefined {
  return allVideos.find((v) => v.id === id);
}

export function getVideosByCategory(slug: string): Video[] {
  return allVideos.filter((v) => v.category === slug);
}

export function getHotVideos(limit = 6): Video[] {
  return [...allVideos].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestVideos(limit = 8): Video[] {
  return [...allVideos]
    .sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1))
    .slice(0, limit);
}

export function getRelatedVideos(video: Video, limit = 6): Video[] {
  const sameCategory = allVideos.filter(
    (v) => v.category === video.category && v.id !== video.id,
  );
  if (sameCategory.length >= limit) {
    return sameCategory.slice(0, limit);
  }
  const others = allVideos.filter((v) => v.category !== video.category);
  return [...sameCategory, ...others].slice(0, limit);
}

export function formatViews(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}千`;
  }
  return String(value);
}

export function searchVideos(keyword: string): Video[] {
  const k = keyword.trim().toLowerCase();
  if (!k) {
    return [];
  }
  return allVideos.filter(
    (v) =>
      v.title.toLowerCase().includes(k) ||
      v.tags.some((tag) => tag.toLowerCase().includes(k)) ||
      v.author.toLowerCase().includes(k) ||
      v.description.toLowerCase().includes(k),
  );
}

export type SortKey = "latest" | "hot" | "duration";

export function sortVideos(list: Video[], sort: SortKey): Video[] {
  const copy = [...list];
  if (sort === "hot") {
    return copy.sort((a, b) => b.views - a.views);
  }
  if (sort === "duration") {
    return copy.sort((a, b) => b.durationSeconds - a.durationSeconds);
  }
  return copy.sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1));
}

export const timeFilterOptions = [
  { value: "all", label: "全部时间" },
  { value: "week", label: "一周内" },
  { value: "month", label: "一个月内" },
  { value: "year", label: "一年内" },
];

export const durationFilterOptions = [
  { value: "all", label: "全部时长", min: 0, max: Infinity },
  { value: "short", label: "5 分钟以内", min: 0, max: 300 },
  { value: "medium", label: "5 - 15 分钟", min: 300, max: 900 },
  { value: "long", label: "15 分钟以上", min: 900, max: Infinity },
];

export function isWithinTime(uploadDate: string, range: string): boolean {
  if (range === "all") {
    return true;
  }
  const now = new Date("2026-10-14");
  const target = new Date(uploadDate);
  const diffDays = (now.getTime() - target.getTime()) / (1000 * 60 * 60 * 24);
  if (range === "week") {
    return diffDays <= 7;
  }
  if (range === "month") {
    return diffDays <= 31;
  }
  if (range === "year") {
    return diffDays <= 366;
  }
  return true;
}

export function isWithinDuration(seconds: number, range: string): boolean {
  const option = durationFilterOptions.find((o) => o.value === range);
  if (!option || option.value === "all") {
    return true;
  }
  return seconds >= option.min && seconds < option.max;
}

export function collectTags(list: Video[]): string[] {
  const set = new Set<string>();
  list.forEach((v) => v.tags.forEach((t) => set.add(t)));
  return Array.from(set);
}

export function formatCountUp(value: number): string {
  return new Intl.NumberFormat("zh-CN").format(value);
}